import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, Button, Typography, Descriptions, 
  Space, message, Modal 
} from 'antd';
import { LockOutlined, EditOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Title } = Typography;

const Profile = () => {
  const { user, changePassword } = useAuth();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();

  // 如果是首次登入，自動打開修改密碼 Modal
  useEffect(() => {
    if (user?.firstLogin) {
      setPasswordModalVisible(true);
      message.info('首次登入，請先修改密碼');
    }
  }, [user]);

  const handleChangePassword = async (values) => {
    const result = await changePassword(values.oldPassword, values.newPassword);
    
    if (result.success) {
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } else {
      message.error(result.error || '修改密碼失敗');
    }
  };

  const getLevelName = (level) => {
    const levelNames = {
      0: '管理員',
      1: '一級代理',
      2: '二級代理',
      3: '三級代理',
      4: '四級代理',
      5: '五級代理'
    };
    return levelNames[level] || `Level ${level}`;
  };

  return (
    <div>
      <Title level={2}>個人資料</Title>
      
      <Card title="基本資訊" style={{ marginBottom: 16 }}>
        <Descriptions column={2}>
          <Descriptions.Item label="帳號">
            {user?.username}
          </Descriptions.Item>
          <Descriptions.Item label="顯示名稱">
            {user?.displayName}
          </Descriptions.Item>
          <Descriptions.Item label="代理層級">
            {getLevelName(user?.level)}
          </Descriptions.Item>
          <Descriptions.Item label="狀態">
            {user?.status === 'active' ? '正常' : '停用'}
          </Descriptions.Item>
          <Descriptions.Item label="上級代理" span={2}>
            {user?.parent ? `${user.parent.displayName} (${user.parent.username})` : '無'}
          </Descriptions.Item>
          <Descriptions.Item label="註冊時間" span={2}>
            {user?.createdAt ? new Date(user.createdAt).toLocaleString('zh-TW') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="最後登入" span={2}>
            {user?.lastLogin ? new Date(user.lastLogin).toLocaleString('zh-TW') : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      
      <Card title="帳戶資訊" style={{ marginBottom: 16 }}>
        <Descriptions column={2}>
          <Descriptions.Item label="信用額度">
            ${(user?.creditLimit || 0).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="已使用額度">
            ${(user?.usedCredit || 0).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="可用額度">
            ${((user?.creditLimit || 0) - (user?.usedCredit || 0)).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="佔成率">
            {user?.commissionRate || 0}%
          </Descriptions.Item>
          <Descriptions.Item label="洗碼佣金率">
            {user?.rebateRate || 0}%
          </Descriptions.Item>
        </Descriptions>
      </Card>
      
      <Card title="安全設定">
        <Space>
          <Button
            type="primary"
            icon={<LockOutlined />}
            onClick={() => setPasswordModalVisible(true)}
          >
            修改密碼
          </Button>
        </Space>
      </Card>
      
      {/* 修改密碼 Modal */}
      <Modal
        title="修改密碼"
        open={passwordModalVisible}
        onCancel={() => {
          // 首次登入時不允許關閉
          if (!user?.firstLogin) {
            setPasswordModalVisible(false);
            passwordForm.resetFields();
          }
        }}
        closable={!user?.firstLogin}
        maskClosable={!user?.firstLogin}
        footer={null}
      >
        <Form
          form={passwordForm}
          onFinish={handleChangePassword}
          layout="vertical"
        >
          <Form.Item
            name="oldPassword"
            label="舊密碼"
            rules={[{ required: true, message: '請輸入舊密碼' }]}
          >
            <Input.Password placeholder="請輸入舊密碼" />
          </Form.Item>
          
          <Form.Item
            name="newPassword"
            label="新密碼"
            rules={[
              { required: true, message: '請輸入新密碼' },
              { min: 6, message: '密碼至少6位' }
            ]}
          >
            <Input.Password placeholder="請輸入新密碼" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="確認新密碼"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '請確認新密碼' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('兩次輸入的密碼不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="請再次輸入新密碼" />
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              {!user?.firstLogin && (
                <Button onClick={() => setPasswordModalVisible(false)}>
                  取消
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                確認修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;